import type { ActionReturn } from "svelte/action";

/** Overlap (px) below which a visual-viewport shrink is treated as browser chrome, not the keyboard. */
const keyboardThreshold = 100;

/**
 * Keeps a `--keyboard-inset` custom property on the element in sync with how much the iOS soft
 * keyboard overlaps the layout viewport, so a bottom-anchored modal can pad its content above the
 * keyboard. iOS Safari does not resize the layout viewport for the keyboard, so `100dvh` sizing
 * leaves the lower part of the modal (last fields, sticky footer) stuck behind it.
 *
 * No-op when the VisualViewport API is unavailable or the keyboard is closed; the property then
 * stays `0px`, leaving the layout unchanged.
 */
export function keyboardInset(node: HTMLElement, enabled: boolean): ActionReturn<boolean> {
	const viewport = window.visualViewport;
	let listening = false;

	function apply(): void {
		if (!viewport) {
			return;
		}
		const overlap = window.innerHeight - viewport.height - viewport.offsetTop;
		node.style.setProperty("--keyboard-inset", overlap > keyboardThreshold ? `${overlap}px` : "0px");
	}

	function start(): void {
		if (!viewport || listening) {
			return;
		}
		listening = true;
		viewport.addEventListener("resize", apply);
		viewport.addEventListener("scroll", apply);
		apply();
	}

	function stop(): void {
		listening = false;
		viewport?.removeEventListener("resize", apply);
		viewport?.removeEventListener("scroll", apply);
		node.style.setProperty("--keyboard-inset", "0px");
	}

	if (enabled) {
		start();
	}

	return {
		update(next: boolean): void {
			if (next) {
				start();
			} else {
				stop();
			}
		},
		destroy(): void {
			stop();
		},
	};
}
